// ============================================================================
//  Chat v2 — Page principale complète
//  Nouvelles fonctionnalités :
//  - Mentions @quelqu'un avec autocomplétion dropdown
//  - Sélecteur d'émojis complet (80 émojis custom, zéro dépendance)
//  - Gestion des membres du canal (ajout/suppression)
//  - Upload et partage de fichiers (images + documents, max 25MB)
//  - Prévisualisation inline des images
//  - Indicateur de frappe
//  - Réactions rapides + picker complet
//  - Fil de threads (parentId)
// ============================================================================

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { usersApi } from '@/modules/users/api/users.api'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Hash,
  Lock,
  Plus,
  Search,
  X,
  Send,
  SmilePlus,
  Paperclip,
  ChevronDown,
  MessageSquare,
  Edit2,
  Trash2,
  Users,
  Image as ImageIcon,
  File,
  Download,
  MoreVertical,
  Pin,
  UserPlus,
  ChevronRight,
  Check,
  AtSign,
} from 'lucide-react'
import { chatApi, type Channel, type Message, type Workspace } from '@/modules/chat/api/chat.api'
import { useAuthStore } from '@/stores/auth.store'
import { getSocket, connectSocket } from '@/lib/socket'
import api from '@/lib/axios'

// ─── Types ───────────────────────────────────────────────────────────────────

interface MemberUser {
  id: string
  firstName: string
  lastName: string
  avatarUrl?: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtTime = (d: string) =>
  new Date(d).toLocaleTimeString('fr-SN', { hour: '2-digit', minute: '2-digit' })
const fmtDay = (d: string) => {
  const date = new Date(d)
  const now = new Date()
  if (date.toDateString() === now.toDateString()) return "Aujourd'hui"
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return 'Hier'
  return date.toLocaleDateString('fr-SN', { weekday: 'long', day: 'numeric', month: 'long' })
}
const sameDay = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString()
const fmtSize = (b: number) =>
  b < 1024 * 1024 ? `${(b / 1024).toFixed(0)}KB` : `${(b / 1024 / 1024).toFixed(1)}MB`
const isImage = (mime: string) => mime.startsWith('image/')

// ── Résolution URL pièce jointe ────────────────────────────────────────────
// fileUrl en BDD = chemin MinIO (ex: chat/xxx/2026/abc-img.jpg)
// → besoin d'une URL presignée pour afficher/télécharger
function useAttachmentUrl(messageId: string, fileUrl: string): string {
  const [resolvedUrl, setResolvedUrl] = React.useState<string>('')

  React.useEffect(() => {
    if (!fileUrl) return
    // Si c'est déjà une URL HTTP complète, l'utiliser directement
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      setResolvedUrl(fileUrl)
      return
    }
    // Sinon demander une URL presignée au backend
    import('@/lib/axios').then(({ default: api }) => {
      api.get(`/chat/attachments/${messageId}/url`)
        .then(r => setResolvedUrl(r.data?.url ?? r.data?.data?.url ?? ''))
        .catch(() => setResolvedUrl(''))
    })
  }, [messageId, fileUrl])

  return resolvedUrl
}

// Composant pour afficher une pièce jointe avec URL résolue
function AttachmentItem({ att, messageId }: { att: any; messageId: string }) {
  const url = useAttachmentUrl(messageId, att.fileUrl)

  if (!url) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg mt-1" style={{ background: '#F4F2FC', border: '1px solid var(--bd)' }}>
        <div className="w-5 h-5 rounded border-2 border-[var(--blue)] border-t-transparent animate-spin" />
        <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{att.fileName}</span>
      </div>
    )
  }

  if (isImage(att.mimeType)) {
    return (
      <div className="inline-block mt-1.5">
        <img
          src={url}
          alt={att.fileName}
          className="rounded-xl object-cover cursor-pointer hover:opacity-90 transition-opacity"
          style={{ maxHeight: 280, maxWidth: 400, border: '1px solid var(--bd)', display: 'block' }}
          onClick={() => window.open(url, '_blank')}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
        />
        <p style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 4 }}>
          {att.fileName} · {fmtSize(att.fileSize)}
        </p>
      </div>
    )
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-3 px-3 py-2.5 rounded-xl hover:opacity-80 transition-opacity mt-1.5"
      style={{ background: '#F4F2FC', border: '1px solid var(--bd)', maxWidth: 280, display: 'inline-flex' }}
    >
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--bd)' }}>
        <File className="w-4 h-4" style={{ color: 'var(--brand-amber)' }} />
      </div>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-dark)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: 180 }}>{att.fileName}</p>
        <p style={{ fontSize: 11, color: 'var(--tx3)' }}>{fmtSize(att.fileSize)}</p>
      </div>
      <Download className="w-4 h-4 shrink-0" style={{ color: 'var(--tx3)' }} />
    </a>
  )
}

// ─── Emoji Picker (custom, zéro dépendance) ────────────────────────────────

const EMOJI_CATEGORIES = [
  {
    label: '😀 Visages',
    emojis: [
      '😀',
      '😁',
      '😂',
      '🤣',
      '😃',
      '😄',
      '😅',
      '😆',
      '😇',
      '😉',
      '😊',
      '🙂',
      '🙃',
      '😋',
      '😌',
      '😍',
      '🥰',
      '😘',
      '😗',
      '😙',
      '😚',
      '🤗',
      '🤩',
      '🤔',
      '🤨',
      '😐',
      '😑',
      '😶',
      '🙄',
      '😏',
      '😒',
      '😞',
      '😔',
      '😟',
      '😕',
      '🙁',
      '☹️',
      '😣',
      '😖',
      '😫',
      '😩',
      '🥺',
      '😢',
      '😭',
      '😤',
      '😠',
      '😡',
      '🤬',
      '🤯',
      '😳',
      '🥵',
      '🥶',
      '😱',
      '😨',
      '😰',
      '😥',
      '😓',
      '🤗',
      '🫡',
      '🤫',
      '🫠',
    ],
  },
  {
    label: '👋 Gestes',
    emojis: [
      '👋',
      '🤚',
      '🖐',
      '✋',
      '🖖',
      '👌',
      '🤌',
      '🤏',
      '✌️',
      '🤞',
      '🤟',
      '🤘',
      '🤙',
      '👈',
      '👉',
      '👆',
      '👇',
      '☝️',
      '👍',
      '👎',
      '✊',
      '👊',
      '🤛',
      '🤜',
      '👏',
      '🙌',
      '👐',
      '🤲',
      '🙏',
      '✍️',
      '💪',
      '🦾',
      '🦿',
      '🦵',
      '🦶',
      '👂',
      '🦻',
      '👃',
    ],
  },
  {
    label: '❤️ Cœurs',
    emojis: [
      '❤️',
      '🧡',
      '💛',
      '💚',
      '💙',
      '💜',
      '🖤',
      '🤍',
      '🤎',
      '💔',
      '❣️',
      '💕',
      '💞',
      '💓',
      '💗',
      '💖',
      '💘',
      '💝',
      '💟',
      '☮️',
      '✝️',
      '☪️',
      '🕉',
      '✡️',
      '🔯',
      '🕎',
      '☯️',
      '☦️',
      '🛐',
      '⛎',
      '♈',
      '♉',
      '♊',
      '♋',
      '♌',
      '♍',
      '♎',
      '♏',
    ],
  },
  {
    label: '🎉 Fête',
    emojis: [
      '🎉',
      '🎊',
      '🎈',
      '🎁',
      '🎀',
      '🎗',
      '🎟',
      '🎫',
      '🏆',
      '🥇',
      '🥈',
      '🥉',
      '🏅',
      '🎖',
      '🥳',
      '🎂',
      '🍰',
      '🧁',
      '🍭',
      '🍬',
      '🍫',
      '🍿',
      '🍩',
      '🍪',
      '🎄',
      '🎃',
      '🎆',
      '🎇',
      '✨',
      '🌟',
      '⭐',
      '💫',
      '🌠',
      '🎑',
      '🎐',
      '🎏',
      '🎍',
    ],
  },
  {
    label: '💼 Travail',
    emojis: [
      '💼',
      '📊',
      '📈',
      '📉',
      '📋',
      '📌',
      '📍',
      '📎',
      '🖇',
      '📏',
      '📐',
      '✂️',
      '🗂',
      '🗃',
      '🗄',
      '🗑',
      '🔒',
      '🔓',
      '🔏',
      '🔐',
      '🔑',
      '🗝',
      '🔨',
      '⚒',
      '🛠',
      '⛏',
      '🔧',
      '🔩',
      '⚙️',
      '🗜',
      '💡',
      '🔦',
      '🕯',
      '🖊',
      '🖋',
      '✒️',
      '📝',
      '✏️',
    ],
  },
  {
    label: '✅ Statuts',
    emojis: [
      '✅',
      '❌',
      '⭕',
      '🔴',
      '🟠',
      '🟡',
      '🟢',
      '🔵',
      '🟣',
      '⚫',
      '⚪',
      '🟤',
      '🔶',
      '🔷',
      '🔸',
      '🔹',
      '🔺',
      '🔻',
      '💠',
      '🔘',
      '🔲',
      '🔳',
      '▪️',
      '▫️',
      '◾',
      '◽',
      '◼️',
      '◻️',
      '🚀',
      '⚡',
      '🔥',
      '💥',
      '❗',
      '❓',
      '‼️',
      '⁉️',
      '💯',
      '🆗',
      '🆕',
      '🆙',
      '🆒',
      '🆓',
      '🆖',
      '🅰️',
      '🅱️',
      '🆎',
      '🆑',
    ],
  },
]

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '🚀', '👀', '✅', '💯']

function EmojiPicker({
  onSelect,
  onClose,
}: {
  onSelect: (e: string) => void
  onClose: () => void
}) {
  const [cat, setCat] = useState(0)
  const [search, setSearch] = useState('')

  const filtered = search
    ? EMOJI_CATEGORIES.flatMap((c) => c.emojis).filter((e) => e.includes(search))
    : EMOJI_CATEGORIES[cat].emojis

  return (
    <div
      className="nx-card shadow-2xl w-72 overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Search */}
      <div className="p-2 border-b border-[var(--bd)]">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un emoji…"
          autoFocus
          className="w-full px-3 py-1.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
        />
      </div>

      {/* Categories */}
      {!search && (
        <div className="flex gap-1 px-2 py-1.5 border-b border-[var(--bd)] overflow-x-auto">
          {EMOJI_CATEGORIES.map((c, i) => (
            <button
              key={i}
              onClick={() => setCat(i)}
              className={`text-sm px-2 py-1 rounded-lg transition-colors whitespace-nowrap ${cat === i ? 'bg-[var(--blue)]/20 text-blue-300' : 'text-[var(--tx3)] hover:text-white hover:bg-[var(--surface-2)]'}`}
            >
              {c.label.split(' ')[0]}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-8 gap-0.5 p-2 max-h-48 overflow-y-auto">
        {filtered.map((e) => (
          <button
            key={e}
            onClick={() => {
              onSelect(e)
              onClose()
            }}
            className="text-xl p-1 rounded hover:bg-[var(--surface-2)] transition-colors leading-none aspect-square flex items-center justify-center"
            title={e}
          >
            {e}
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-8 text-center text-[var(--tx3)] text-xs py-4">
            Aucun emoji trouvé
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({
  user,
  size = 'sm',
  status,
}: {
  user?: MemberUser
  size?: 'xs' | 'sm' | 'md'
  status?: string
}) {
  if (!user) return <div className="w-8 h-8 rounded-full bg-[var(--surface-2)]" />
  const s = { xs: 'w-5 h-5 text-[10px]', sm: 'w-7 h-7 text-[11px]', md: 'w-9 h-9 text-sm' }[size]
  const initials = `${user.firstName[0]}${user.lastName[0]}`
  const statusColor: Record<string, string> = {
    ONLINE: 'bg-[var(--green)]',
    AWAY: 'bg-[var(--amber)]',
    DND: 'bg-[#F87171]',
    OFFLINE: 'bg-[var(--surface-3)]',
  }
  return (
    <div className="relative shrink-0">
      {user.avatarUrl ? (
        <img src={user.avatarUrl} alt={initials} className={`${s} rounded-full object-cover`} />
      ) : (
        <div
          className={`${s} rounded-full flex items-center justify-center font-bold`} style={{ background: 'linear-gradient(135deg, var(--brand-orange), var(--brand-amber))', color: '#FFFFFF' }}
        >
          {initials}
        </div>
      )}
      {status && (
        <div
          className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${statusColor[status] ?? 'bg-[var(--surface-3)]'} border-2 border-[var(--bd)]`}
        />
      )}
    </div>
  )
}

// ============================================================================
//  CHAT PAGE — Layout principal
// ============================================================================

export default function ChatPage() {
  const { channelId } = useParams<{ channelId?: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [showNewChannel, setShowNewChannel] = useState(false)
  const [showNewDirect, setShowNewDirect] = useState(false)
  const [showMembers, setShowMembers] = useState(false)
  const socket = useMemo(() => getSocket(), [])

  const { data: workspaces = [] } = useQuery({
    queryKey: ['chat-workspaces'],
    queryFn: () => chatApi.getWorkspaces(),
  })

  const workspace: Workspace | undefined = workspaces[0]
  const channels: Channel[] = workspace?.channels ?? []

  // Socket connection lifecycle
  useEffect(() => {
    if (!user) return
    // FIX : connectSocket() met à jour le token AVANT de connecter
    // Nécessaire car getSocket() est un singleton créé au 1er render,
    // le token peut être null à ce moment si le composant monte tôt.
    connectSocket()
    socket.on('message:new', (data: { channelId: string; message: Message }) => {
      queryClient.setQueryData<{ data: Message[]; hasMore: boolean }>(['messages', data.channelId], (prev) =>
        prev
          ? { ...prev, data: [...(prev.data ?? []), data.message] }
          : { data: [data.message], hasMore: false },
      )
      if (data.channelId !== channelId) {
        queryClient.invalidateQueries({ queryKey: ['chat-workspaces'] })
      }
    })
    socket.on('message:updated', (data: { channelId: string; message: Message }) => {
      queryClient.setQueryData<{ data: Message[]; hasMore: boolean }>(['messages', data.channelId], (prev) =>
        prev
          ? {
              ...prev,
              data: (prev.data ?? []).map((m) => (m.id === data.message.id ? data.message : m)),
            }
          : prev,
      )
    })
    socket.on(
      'message:deleted',
      ({ messageId, channelId: cId }: { messageId: string; channelId: string }) => {
        queryClient.setQueryData<{ data: Message[]; hasMore: boolean }>(['messages', cId], (prev) =>
          prev
            ? {
                ...prev,
                data: (prev.data ?? []).map((m) =>
                  m.id === messageId
                    ? { ...m, deletedAt: new Date().toISOString(), content: '[Message supprimé]' }
                    : m,
                ),
              }
            : prev,
        )
      },
    )
    socket.on('reaction:updated', ({ channelId: cId }: any) => {
      queryClient.invalidateQueries({ queryKey: ['messages', cId] })
    })
    return () => {
      socket.off('message:new')
      socket.off('message:updated')
      socket.off('message:deleted')
      socket.off('reaction:updated')
      socket.disconnect()
    }
  }, [user?.id])

  useEffect(() => {
    if (channelId) {
      socket.emit('channel:join', { channelId })
      socket.emit('channel:mark_read', { channelId })
    }
  }, [channelId])

  useEffect(() => {
    if (!channelId && channels.length > 0) navigate(`/chat/${channels[0].id}`, { replace: true })
  }, [channelId, channels])

  const publicChannels = channels.filter((c) => ['PUBLIC', 'PRIVATE'].includes(c.type))
  const directChannels = channels.filter((c) => ['DIRECT', 'GROUP'].includes(c.type))
  const filtered = (list: Channel[]) =>
    search ? list.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())) : list

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden rounded-xl border border-[var(--bd)]">
      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <div className="shrink-0 flex flex-col" style={{ background: 'var(--sb-bg)', width: 240 }}>
        <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <button className="flex items-center justify-between w-full" style={{ color: '#FFFFFF' }}>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded flex items-center justify-center text-xs font-bold" style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}>
                {(workspace?.name ?? 'N')[0]}
              </div>
              <span className="text-sm font-bold" style={{ letterSpacing: '-0.01em' }}>{workspace?.name ?? 'NexusCorp'}</span>
            </div>
            <ChevronDown className="w-3 h-3" style={{ color: 'var(--tx3)' }} />
          </button>
        </div>
        <div className="px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--tx3)' }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="w-full pl-7 pr-3 py-1.5 rounded text-xs focus:outline-none"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--bd)' }}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-4" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>
          <ChannelSection
            title="Canaux"
            channels={filtered(publicChannels)}
            activeId={channelId}
            onSelect={(id) => navigate(`/chat/${id}`)}
            onAdd={() => setShowNewChannel(true)}
            currentUserId={user?.id}
          />
          <ChannelSection
            title="Messages directs"
            channels={filtered(directChannels)}
            activeId={channelId}
            onSelect={(id) => navigate(`/chat/${id}`)}
            onAdd={() => setShowNewDirect(true)}
            isDirect
            currentUserId={user?.id}
          />
        </div>
      </div>

      {/* ── Zone principale ───────────────────────────────────────────────── */}
      {channelId ? (
        <MessageArea
          key={channelId}
          channelId={channelId}
          workspaceId={workspace?.id}
          socket={socket}
          onShowMembers={() => setShowMembers(true)}
          currentUserId={user?.id}
        />
      ) : (
        <div className="flex-1 flex items-center justify-center text-[var(--tx3)]">
          <div className="text-center">
            <MessageSquare className="w-12 h-12 mb-3 opacity-20 mx-auto" />
            <p className="text-sm">Sélectionnez un canal</p>
          </div>
        </div>
      )}

      {/* ── Panel membres ────────────────────────────────────────────────── */}
      {showMembers && channelId && (
        <MembersPanel channelId={channelId} onClose={() => setShowMembers(false)} />
      )}

      {showNewChannel && workspace && (
        <NewChannelModal
          workspaceId={workspace.id}
          onClose={() => setShowNewChannel(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['chat-workspaces'] })
            setShowNewChannel(false)
          }}
        />
      )}
      {showNewDirect && workspace && (
        <NewDirectModal
          workspaceId={workspace.id}
          onClose={() => setShowNewDirect(false)}
          onCreated={(channelId: string) => {
            queryClient.invalidateQueries({ queryKey: ['chat-workspaces'] })
            setShowNewDirect(false)
            navigate(`/chat/${channelId}`)
          }}
        />
      )}
    </div>
  )
}

// ─── Helper : nom affiché pour un canal DM ───────────────────────────────────
// Pour les canaux DIRECT, afficher le prénom+nom de l'autre membre
// plutôt que le nom technique (userId1-dm-userId2)
function getDmDisplayName(channel: Channel, currentUserId?: string): string {
  if (channel.type !== 'DIRECT' && channel.type !== 'GROUP') return channel.name
  const other = channel.members?.find((m) => m.userId !== currentUserId)
  if (other?.user) return `${other.user.firstName} ${other.user.lastName}`
  return channel.name
}


// ─── Channel Section ──────────────────────────────────────────────────────────

function ChannelSection({
  title,
  channels,
  activeId,
  onSelect,
  onAdd,
  isDirect,
  currentUserId,
}: {
  title: string
  channels: Channel[]
  activeId?: string
  onSelect: (id: string) => void
  onAdd?: () => void
  isDirect?: boolean
  currentUserId?: string
}) {
  return (
    <div>
      <div className="flex items-center justify-between px-2 py-1">
        <span className="text-[11px] font-semibold text-[var(--tx3)] uppercase tracking-wider">
          {title}
        </span>
        {onAdd && (
          <button onClick={onAdd} className="p-0.5 hover:bg-[var(--surface-2)] rounded">
            <Plus className="w-3.5 h-3.5 text-[var(--tx3)] hover:text-[var(--tx)]" />
          </button>
        )}
      </div>
      <div className="space-y-0.5">
        {channels.map((c) => {
          const isActive = c.id === activeId
          return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              className="w-full flex items-center gap-2 py-1.5 rounded-md text-xs transition-all text-left"
              style={{
                color: isActive ? '#FFFFFF' : '#A09BC0',
                background: isActive ? 'rgba(245,124,0,0.18)' : 'transparent',
                fontWeight: isActive ? 700 : 400,
                paddingLeft: isActive ? 7 : 8,
                paddingRight: 8,
                borderLeft: isActive ? '2.5px solid var(--brand-orange)' : '2.5px solid transparent',
              }}
            >
              {isDirect ? (
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: '#3BA55D' }} />
              ) : c.type === 'PRIVATE' ? (
                <Lock className="w-3.5 h-3.5 shrink-0" />
              ) : (
                <Hash className="w-3.5 h-3.5 shrink-0" />
              )}
              <span className="flex-1 truncate">{getDmDisplayName(c, currentUserId)}</span>
              {(c.unreadCount ?? 0) > 0 && (
                <span className="min-w-[18px] px-1 py-0.5 bg-[var(--blue)] text-white text-[10px] font-bold rounded-full text-center">
                  {c.unreadCount! > 99 ? '99+' : c.unreadCount}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── Message Area ─────────────────────────────────────────────────────────────

function MessageArea({
  channelId,
  workspaceId,
  socket,
  onShowMembers,
  currentUserId,
}: {
  channelId: string
  workspaceId?: string
  socket: ReturnType<typeof getSocket>
  onShowMembers: () => void
  currentUserId?: string
}) {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  // Input state
  const [input, setInput] = useState('')
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [emojiForMessage, setEmojiForMessage] = useState<string | null>(null) // messageId pour reaction picker
  const [typing, setTyping] = useState<string[]>([])
  const [replyTo, setReplyTo] = useState<Message | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // ── Auto-resize textarea ──────────────────────────────────────────────────
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'            // reset pour recalculer
    el.style.height = Math.min(el.scrollHeight, 160) + 'px'  // max ~6 lignes
  }, [input])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const typingTimeout = useRef<ReturnType<typeof setTimeout>>()

  const { data: channel } = useQuery({
    queryKey: ['channel', channelId],
    queryFn: () => chatApi.getChannel(channelId),
  })

  const { data: messagesData, isLoading } = useQuery({
    queryKey: ['messages', channelId],
    queryFn: () => chatApi.getMessages(channelId, { limit: 60 }),
    refetchOnWindowFocus: false,
  })

  const { data: membersData } = useQuery({
    queryKey: ['channel-members', channelId],
    queryFn: () => chatApi.getMembers(channelId),
  })

  const messages: Message[] = messagesData?.data ?? []
  // FIX: getMembers retourne un tableau direct [], pas { members: [] }
  const members: any[] = Array.isArray(membersData) ? membersData : (membersData?.members ?? [])
  const memberUsers: MemberUser[] = members.map((m) => m.user).filter(Boolean)

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  // Typing
  useEffect(() => {
    socket.on('user:typing', ({ userId, isTyping: iT, user: u }: any) => {
      if (userId === user?.id) return
      const name = u?.firstName ?? "Quelqu'un"
      setTyping((prev) => (iT ? [...new Set([...prev, name])] : prev.filter((n) => n !== name)))
    })
    return () => {
      socket.off('user:typing')
    }
  }, [user?.id])

  // Mentions autocomplétion — filtrer selon la query
  const mentionSuggestions = useMemo(() => {
    if (!mentionQuery) return []
    const q = mentionQuery.toLowerCase()
    return memberUsers
      .filter((m) => `${m.firstName} ${m.lastName}`.toLowerCase().includes(q) && m.id !== user?.id)
      .slice(0, 6)
  }, [mentionQuery, memberUsers])

  // ── Input handlers ────────────────────────────────────────────────────────

  const handleInput = (val: string) => {
    setInput(val)
    // Détecter @mention : dernier @ suivi de caractères sans espace
    const match = val.match(/@(\w*)$/)
    setMentionQuery(match ? match[1] : null)
    setMentionIndex(0)

    // Typing indicator
    socket.emit('typing:start', { channelId })
    clearTimeout(typingTimeout.current)
    typingTimeout.current = setTimeout(() => socket.emit('typing:stop', { channelId }), 3000)
  }

  const insertMention = (m: MemberUser) => {
    const newText = input.replace(/@\w*$/, `@${m.firstName} `)
    setInput(newText)
    setMentionQuery(null)
    inputRef.current?.focus()
  }

  const insertEmoji = (emoji: string) => {
    const el = inputRef.current
    if (!el) {
      setInput((p) => p + emoji)
      return
    }
    const start = el.selectionStart ?? input.length
    const end = el.selectionEnd ?? input.length
    setInput(input.slice(0, start) + emoji + input.slice(end))
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = start + emoji.length
      el.focus()
    }, 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Navigation dans les mentions
    if (mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIndex((i) => Math.min(i + 1, mentionSuggestions.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIndex((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(mentionSuggestions[mentionIndex])
        return
      }
      if (e.key === 'Escape') {
        setMentionQuery(null)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // ── Envoi message ─────────────────────────────────────────────────────────

  const sendMessage = () => {
    if (!input.trim()) return
    // Extraire les IDs mentionnés
    const mentionIds = memberUsers.filter((m) => input.includes(`@${m.firstName}`)).map((m) => m.id)

    socket.emit('message:send', {
      channelId,
      content: input.trim(),
      mentionIds: mentionIds.length ? mentionIds : undefined,
      parentId: replyTo?.id,
    })
    setInput('')
    setReplyTo(null)
    setMentionQuery(null)
    socket.emit('typing:stop', { channelId })
    // Reset textarea height after send
    if (inputRef.current) {
      inputRef.current.style.height = '40px'
    }
  }

  // ── Upload fichier ────────────────────────────────────────────────────────

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      if (input.trim()) fd.append('content', input.trim())
      return api
        .post(`/chat/channels/${channelId}/messages/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data.data)
    },
    onSuccess: () => {
      setInput('')
      queryClient.invalidateQueries({ queryKey: ['messages', channelId] })
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 25 * 1024 * 1024) {
      alert('Fichier trop grand (max 25MB)')
      return
    }
    uploadMutation.mutate(file)
    e.target.value = ''
  }

  // ── Réaction ──────────────────────────────────────────────────────────────

  const addReaction = (messageId: string, emoji: string) => {
    socket.emit('reaction:add', { messageId, channelId, emoji })
    setEmojiForMessage(null)
  }

  return (
    <div className="flex-1 flex flex-col min-w-0" style={{ background: '#FFFFFF' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 shrink-0" style={{ background: '#FFFFFF', borderBottom: '1px solid var(--bd)', minHeight: 52 }}>
        <div className="flex items-center gap-2">
          {channel?.type === 'PRIVATE' ? (
            <Lock className="w-4 h-4" style={{ color: 'var(--tx3)' }} />
          ) : (
            <Hash className="w-4 h-4" style={{ color: 'var(--brand-orange)' }} />
          )}
          <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--brand-dark)', letterSpacing: '-0.02em' }}>{channel ? getDmDisplayName(channel as any, currentUserId) : '…'}</span>
          {channel?.description && (
            <span style={{ fontSize: 12, color: 'var(--tx3)', borderLeft: '1px solid var(--bd)', paddingLeft: 10, marginLeft: 2 }} className="hidden md:block">
              {channel.description}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onShowMembers}
            className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-[var(--surface-2)] rounded-lg transition-colors text-xs text-[var(--tx3)] hover:text-[var(--tx)]"
          >
            <Users className="w-4 h-4" /> {members.length}
          </button>
          <button className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
            <Search className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto py-4 space-y-0"
        onClick={() => {
          setShowEmojiPicker(false)
          setEmojiForMessage(null)
        }}
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[var(--tx3)]">
            <Hash className="w-10 h-10 mb-3 opacity-20" />
            <p className="text-sm font-medium">Début de {channel?.type === 'DIRECT' ? '' : '#'}{channel ? getDmDisplayName(channel as any, currentUserId) : '…'}</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const prev = messages[i - 1]
            const showDay = !prev || !sameDay(prev.createdAt, msg.createdAt)
            const isOwn = msg.authorId === user?.id
            const consecutive =
              !showDay &&
              !!prev &&
              prev.authorId === msg.authorId &&
              new Date(msg.createdAt).getTime() - new Date(prev.createdAt).getTime() < 5 * 60 * 1000

            return (
              <div key={msg.id}>
                {showDay && (
                  <div className="flex items-center gap-3 py-4">
                    <div className="flex-1 h-px" style={{ background: '#EDE9F9' }} />
                    <span style={{ fontSize: 11, color: 'var(--tx3)', padding: '2px 12px', background: '#F4F2FC', borderRadius: 20, fontWeight: 500 }}>
                      {fmtDay(msg.createdAt)}
                    </span>
                    <div className="flex-1 h-px" style={{ background: '#EDE9F9' }} />
                  </div>
                )}
                <MessageItem
                  msg={msg}
                  isOwn={isOwn}
                  consecutive={consecutive}
                  onReact={(emoji) => addReaction(msg.id, emoji)}
                  onReactPicker={() =>
                    setEmojiForMessage(emojiForMessage === msg.id ? null : msg.id)
                  }
                  showReactPicker={emojiForMessage === msg.id}
                  onReply={() => setReplyTo(msg)}
                  onEdit={() => {}}
                  currentUserId={user?.id}
                />
              </div>
            )
          })
        )}

        {/* Typing indicator */}
        {typing.length > 0 && (
          <div className="flex items-center gap-2 pl-14 py-1.5">
            <div className="flex gap-0.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 bg-[var(--bd2)] rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
            <span className="text-xs text-[var(--tx3)]">
              {typing.join(', ')} {typing.length === 1 ? 'tape…' : 'tapent…'}
            </span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Zone saisie */}
      <div className="px-4 pb-4 pt-3 shrink-0" style={{ background: '#F8F7FB' }}>
        {/* Reply preview */}
        {replyTo && (
          <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg">
            <div className="w-0.5 h-8 bg-[var(--blue)] rounded-full shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[var(--blue-d)] font-medium">
                {replyTo.author.firstName} {replyTo.author.lastName}
              </p>
              <p className="text-xs text-[var(--tx3)] truncate">{replyTo.content}</p>
            </div>
            <button onClick={() => setReplyTo(null)} className="p-1 hover:bg-[var(--surface-2)] rounded">
              <X className="w-3 h-3 text-[var(--tx3)]" />
            </button>
          </div>
        )}

        {/* Mention suggestions */}
        {mentionSuggestions.length > 0 && (
          <div className="mb-2 nx-card shadow-2xl overflow-hidden">
            {mentionSuggestions.map((m, i) => (
              <button
                key={m.id}
                onClick={() => insertMention(m)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${i === mentionIndex ? 'bg-[var(--blue)]/20' : 'hover:bg-[var(--surface-2)]'}`}
              >
                <Avatar user={m} size="xs" />
                <span className="text-sm text-[var(--tx)]">
                  {m.firstName} {m.lastName}
                </span>
                <span className="text-xs text-[var(--tx3)] ml-auto">@mention</span>
              </button>
            ))}
          </div>
        )}

        {/* Input zone */}
        <div className="relative rounded-2xl transition-all" style={{ background: '#FFFFFF', border: '1.5px solid #E0DCF0', boxShadow: '0 1px 4px rgba(13,8,32,0.06)' }}>
          {/* Emoji Picker flottant */}
          {showEmojiPicker && (
            <div className="absolute bottom-full right-0 mb-2 z-50">
              <EmojiPicker onSelect={insertEmoji} onClose={() => setShowEmojiPicker(false)} />
            </div>
          )}

          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => handleInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Message ${channel?.type === 'DIRECT' ? '' : '#'}${channel ? getDmDisplayName(channel as any, currentUserId) : '…'}`}
            rows={1}
            className="w-full px-4 bg-transparent text-sm focus:outline-none resize-none" 
            style={{ minHeight: 44, maxHeight: 160, lineHeight: '1.55', overflowY: 'auto', paddingTop: 12, paddingBottom: 8, color: 'var(--brand-dark)' }}
          />

          {/* Actions barre du bas */}
          <div className="flex items-center justify-between px-3 pb-2.5 pt-1" style={{ borderTop: '1px solid #F4F2FC' }}>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowEmojiPicker((v) => !v)}
                className={`p-1.5 rounded-lg transition-colors ${showEmojiPicker ? 'bg-[var(--blue)]/20 text-[var(--blue-d)]' : 'text-[var(--tx3)] hover:text-white hover:bg-[var(--surface-2)]'}`}
                title="Emoji"
              >
                <SmilePlus className="w-4 h-4" />
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
                className="p-1.5 rounded-lg transition-colors text-[var(--tx3)] hover:text-[var(--tx)] hover:bg-[var(--surface-2)] disabled:opacity-40"
                title="Joindre un fichier (max 25MB)"
              >
                {uploadMutation.isPending ? (
                  <div className="w-4 h-4 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Paperclip className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => handleInput(input + '@')}
                className="p-1.5 rounded-lg transition-colors text-[var(--tx3)] hover:text-[var(--tx)] hover:bg-[var(--surface-2)]"
                title="Mentionner quelqu'un"
              >
                <AtSign className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={sendMessage}
              disabled={!input.trim() && !uploadMutation.isPending}
              className={`p-1.5 rounded-lg transition-colors ${input.trim() ? 'bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white' : 'text-[var(--tx4)] cursor-not-allowed'}`}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
        />
      </div>
    </div>
  )
}

// ─── Message Item ─────────────────────────────────────────────────────────────

function MessageItem({
  msg,
  isOwn,
  consecutive,
  onReact,
  onReactPicker,
  showReactPicker,
  onReply,
  onEdit,
  currentUserId,
}: {
  msg: Message
  isOwn: boolean
  consecutive: boolean
  onReact: (e: string) => void
  onReactPicker: () => void
  showReactPicker: boolean
  onReply: () => void
  onEdit: () => void
  currentUserId?: string
}) {
  const [showActions, setShowActions] = useState(false)
  const isDeleted = !!msg.deletedAt

  return (
    <div
      className="group relative flex items-start gap-3 px-4 rounded-sm transition-colors"
      style={{ paddingTop: consecutive ? 2 : 12, paddingBottom: 2, marginTop: consecutive ? 0 : 4, background: 'transparent' }}
      onMouseEnter={(e) => { setShowActions(true); (e.currentTarget as HTMLElement).style.background = 'var(--surface-2)' }}
      onMouseLeave={(e) => { setShowActions(false); (e.currentTarget as HTMLElement).style.background = 'transparent' }}
    >
      {/* Avatar ou spacer horodatage */}
      <div className="w-8 shrink-0" style={{ marginTop: consecutive ? 2 : 3 }}>
        {!consecutive ? (
          <Avatar user={msg.author} size="sm" />
        ) : (
          <span
            className="text-[10px] block text-right opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ color: 'var(--tx4)', fontSize: 10, lineHeight: '20px' }}
          >
            {fmtTime(msg.createdAt)}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {!consecutive && (
          <div className="flex items-baseline gap-2 mb-0.5">
            <span className="text-sm font-bold" style={{ color: 'var(--brand-dark)', letterSpacing: '-0.01em' }}>
              {msg.author.firstName} {msg.author.lastName}
            </span>
            <span style={{ fontSize: 11, color: 'var(--tx4)' }}>{fmtTime(msg.createdAt)}</span>
            {msg.editedAt && <span style={{ fontSize: 10, color: 'var(--tx3)' }}>(modifié)</span>}
            {msg.isPinned && <span style={{ fontSize: 11 }}>📌</span>}
          </div>
        )}

        {/* Reply thread preview */}
        {msg.parentId && (
          <div className="flex items-center gap-1 mb-1 text-[10px] text-[var(--tx3)]">
            <div className="w-4 h-px bg-[var(--surface-3)]" />
            <div className="w-px h-3 bg-[var(--surface-3)]" />
            <span className="text-[var(--tx3)]">En réponse à un message</span>
          </div>
        )}

        {/* Texte du message */}
        <p
          className="text-sm whitespace-pre-wrap break-words"
          style={{ color: isDeleted ? 'var(--tx3)' : '#2D2546', fontStyle: isDeleted ? 'italic' : 'normal', lineHeight: '1.55', marginTop: 0 }}
        >
          {/* Rendre les mentions en bleu */}
          {msg.content.split(/(@\w+(?:\s\w+)?)/g).map((part, i) =>
            part.startsWith('@') ? (
              <span key={i} className="text-[var(--blue-d)] font-medium">
                {part}
              </span>
            ) : (
              <span key={i}>{part}</span>
            ),
          )}
        </p>

        {/* Pièces jointes — URL presignée résolue dynamiquement */}
        {msg.attachments && msg.attachments.length > 0 && (
          <div className="mt-1 space-y-1">
            {msg.attachments.map((att: any) => (
              <AttachmentItem key={att.id} att={att} messageId={msg.id} />
            ))}
          </div>
        )}

        {/* Réactions existantes */}
        {msg.reactions && msg.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {Object.values(
              msg.reactions.reduce((acc: any, r: any) => {
                if (!acc[r.emoji]) acc[r.emoji] = { ...r }
                else { acc[r.emoji].count += r.count; acc[r.emoji].hasReacted = acc[r.emoji].hasReacted || r.hasReacted }
                return acc
              }, {})
            ).map((r: any) => (
              <button
                key={r.emoji}
                onClick={() => onReact(r.emoji)}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-all"
              style={{ background: r.hasReacted ? '#EBF7FC' : '#F4F2FC', borderColor: r.hasReacted ? 'var(--brand-orange)' : 'var(--bd)', color: r.hasReacted ? 'var(--blue-d)' : 'var(--tx2)', fontWeight: r.hasReacted ? 600 : 400 }}
              >
                {r.emoji} {r.count}
              </button>
            ))}
            <button
              onClick={onReactPicker}
              className="flex items-center px-1.5 py-0.5 rounded-full text-xs border border-[var(--bd)] text-[var(--tx3)] hover:text-[var(--tx)] hover:bg-[var(--surface-2)] transition-colors"
            >
              <SmilePlus className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Picker réaction inline */}
        {showReactPicker && (
          <div className="mt-1">
            <EmojiPicker onSelect={onReact} onClose={() => {}} />
          </div>
        )}
      </div>

      {/* Actions flottantes */}
      {showActions && !isDeleted && (
        <div className="absolute right-3 top-0 flex items-center gap-0.5 rounded-xl px-1 py-1 z-10" style={{ background: '#FFFFFF', border: '1px solid var(--bd)', boxShadow: '0 4px 20px rgba(13,8,32,0.12)', top: '-18px' }}>
          {QUICK_REACTIONS.slice(0, 5).map((e) => (
            <button
              key={e}
              onClick={() => onReact(e)}
              className="p-1 hover:bg-[var(--surface-2)] rounded text-sm transition-colors"
            >
              {e}
            </button>
          ))}
          <div className="w-px h-4 bg-[var(--surface-2)] mx-0.5" />
          <button
            onClick={onReactPicker}
            className="p-1.5 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
            title="Plus d'émojis"
          >
            <SmilePlus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onReply}
            className="p-1.5 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
            title="Répondre"
          >
            <MessageSquare className="w-3.5 h-3.5" />
          </button>
          {isOwn && (
            <button
              onClick={onEdit}
              className="p-1.5 hover:bg-[var(--surface-2)] rounded text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
              title="Modifier"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Members Panel ────────────────────────────────────────────────────────────

function MembersPanel({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const [search, setSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const queryClient = useQueryClient()

  const { data: membersData } = useQuery({
    queryKey: ['channel-members', channelId],
    queryFn: () => chatApi.getMembers(channelId),
  })

  const addMutation = useMutation({
    mutationFn: (userId: string) => chatApi.addMember(channelId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-members', channelId] })
      setShowAdd(false)
    },
  })

  // FIX: getMembers retourne un tableau direct [], pas { members: [] }
  const members: any[] = Array.isArray(membersData) ? membersData : (membersData?.members ?? [])
  const ROLE_LABEL: Record<string, string> = {
    OWNER: 'Propriétaire',
    ADMIN: 'Admin',
    MEMBER: 'Membre',
    VIEWER: 'Lecteur',
  }

  return (
    <div className="w-64 shrink-0 bg-white border-l border-[var(--bd)] flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bd)]">
        <h3 className="text-sm font-semibold text-[var(--tx)]">Membres ({members.length})</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowAdd(true)}
            className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
            title="Ajouter un membre"
          >
            <UserPlus className="w-4 h-4 text-[var(--tx3)] hover:text-[var(--tx)]" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>
      </div>

      <div className="px-3 py-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher…"
          className="w-full px-3 py-1.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded text-xs text-[var(--tx)] placeholder:text-[var(--tx3)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
        />
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-0.5">
        {members
          .filter(
            (m) =>
              !search ||
              `${m.user.firstName} ${m.user.lastName}`.toLowerCase().includes(search.toLowerCase()),
          )
          .map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-[var(--surface-2)] transition-colors group"
            >
              <Avatar user={m.user} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[var(--tx)] truncate">
                  {m.user.firstName} {m.user.lastName}
                </p>
                <p className="text-[10px] text-[var(--tx3)]">{ROLE_LABEL[m.role] ?? m.role}</p>
              </div>
            </div>
          ))}
      </div>

      {showAdd && (
        <AddMemberModal
          channelId={channelId}
          existingIds={members.map((m) => m.userId)}
          onAdd={(userId) => addMutation.mutate(userId)}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  )
}

// ─── Add Member Modal ─────────────────────────────────────────────────────────

function AddMemberModal({
  channelId,
  existingIds,
  onAdd,
  onClose,
}: {
  channelId: string
  existingIds: string[]
  onAdd: (userId: string) => void
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const { data } = useQuery({
    queryKey: ['users-search', search],
    queryFn: () => usersApi.directory({ search, limit: 10 }),
    enabled: search.length >= 2,
  })
  const users: any[] = (data ?? []).filter((u: any) => !existingIds.includes(u.id))

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[var(--bd)]">
          <h2 className="text-sm font-semibold text-[var(--tx)]">Ajouter un membre</h2>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un utilisateur…"
            autoFocus
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          />
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {users.map((u: any) => (
              <button
                key={u.id}
                onClick={() => {
                  onAdd(u.id)
                  onClose()
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--surface-2)] text-left transition-colors"
              >
                <Avatar user={u} size="sm" />
                <div>
                  <p className="text-sm text-[var(--tx)]">
                    {u.firstName} {u.lastName}
                  </p>
                  <p className="text-xs text-[var(--tx3)]">{u.email}</p>
                </div>
              </button>
            ))}
            {search.length >= 2 && users.length === 0 && (
              <p className="text-xs text-[var(--tx3)] text-center py-4">Aucun utilisateur trouvé</p>
            )}
            {search.length < 2 && (
              <p className="text-xs text-[var(--tx3)] text-center py-4">Tapez au moins 2 caractères</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── New Channel Modal ────────────────────────────────────────────────────────

function NewChannelModal({
  workspaceId,
  onClose,
  onCreated,
}: {
  workspaceId: string
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const [type, setType] = useState('PUBLIC')
  const { mutate, isPending } = useMutation({
    mutationFn: () => chatApi.createChannel(workspaceId, { name, description: desc, type }),
    onSuccess: onCreated,
  })

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Nouveau canal</h2>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="relative">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tx3)]" />
            <input
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              placeholder="nom-du-canal"
              autoFocus
              className="w-full pl-9 pr-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
            />
          </div>
          <input
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Description (optionnel)"
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          />
          <div className="flex gap-3">
            {[
              ['PUBLIC', 'Public', <Hash className="w-4 h-4" />],
              ['PRIVATE', 'Privé', <Lock className="w-4 h-4" />],
            ].map(([val, label, Icon]: any) => (
              <button
                key={val}
                onClick={() => setType(val)}
                className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${type === val ? 'border-blue-500 bg-[var(--blue-bg)] text-blue-300' : 'border-[var(--bd)] text-[var(--tx3)] hover:border-[var(--bd2)]'}`}
              >
                {Icon} {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-[var(--bd)]">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--tx3)] hover:text-[var(--tx)]">
            Annuler
          </button>
          <button
            onClick={() => mutate()}
            disabled={isPending || !name}
            className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {isPending ? (
              <div className="w-4 h-4 border-2 border-[var(--bd2)] border-t-white rounded-full animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Créer
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── New Direct Message Modal ─────────────────────────────────────────────────

function NewDirectModal({
  workspaceId,
  onClose,
  onCreated,
}: {
  workspaceId: string
  onClose: () => void
  onCreated: (channelId: string) => void
}) {
  const [search, setSearch] = useState('')
  const { mutate, isPending } = useMutation({
    mutationFn: (userId: string) => chatApi.getOrCreateDirect(userId, workspaceId),
    onSuccess: (channel: any) => onCreated(channel.id),
  })

  const { data } = useQuery({
    queryKey: ['users-dm-search', search],
    queryFn: () => usersApi.directory({ search, limit: 10 }),
    enabled: search.length >= 1,
  })
  const users: any[] = data ?? []

  return (
    <div className="fixed inset-0 bg-[rgba(13,8,32,0.50)] backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white border border-[var(--bd)] rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[var(--bd)]">
          <h2 className="text-base font-semibold text-[var(--tx)]">Nouveau message direct</h2>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-[var(--tx3)]" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un utilisateur…"
            autoFocus
            className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
          />
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {users.map((u: any) => (
              <button
                key={u.id}
                onClick={() => mutate(u.id)}
                disabled={isPending}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[var(--surface-2)] text-left transition-colors disabled:opacity-50"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {u.firstName?.[0]}{u.lastName?.[0]}
                </div>
                <div>
                  <p className="text-sm font-medium text-[var(--tx)]">{u.firstName} {u.lastName}</p>
                  <p className="text-xs text-[var(--tx3)]">{u.email}</p>
                </div>
              </button>
            ))}
            {search.length >= 1 && users.length === 0 && (
              <p className="text-xs text-[var(--tx3)] text-center py-4">Aucun utilisateur trouvé</p>
            )}
            {search.length === 0 && (
              <p className="text-xs text-[var(--tx3)] text-center py-4">Tapez pour rechercher…</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
